import {
  type BrandCode,
  BRAND_CODES,
  DEFAULT_BRAND,
  HEALTHSPRING_HOST_TOKEN,
} from './constants';

const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1', '::1'];

const resolveBrandOverrideFromSearch = (
  search: string | undefined | null,
): BrandCode | null => {
  if (!search) {
    return null;
  }

  const queryParams = new URLSearchParams(search);
  const override = (queryParams.get('brand') || queryParams.get('brandCode'))?.toLowerCase();

  if (override === 'healthspring') {
    return BRAND_CODES.HEALTHSPRING;
  }

  if (override === 'bcbs') {
    return BRAND_CODES.BCBS;
  }

  return null;
};

/**
 * Detect brand from hostname.
 *
 * Pure function - no window access. Easy to test and reuse.
 *
 * Rules:
 * - If hostname contains 'healthspring' → return HEALTHSPRING
 * - Otherwise → return BCBS (default)
 */
export const resolveBrandFromHostname = (
  hostname: string | undefined | null,
): BrandCode => {
  if (!hostname) {
    return DEFAULT_BRAND;
  }
  const lowered = hostname.toLowerCase();
  if (lowered.includes(HEALTHSPRING_HOST_TOKEN)) {
    return BRAND_CODES.HEALTHSPRING;
  }
  return DEFAULT_BRAND;
};

/**
 * Resolve brand for local development URLs where a query parameter should
 * override the default hostname-based brand selection.
 */
export const resolveBrandFromLocation = (
  hostname: string | undefined | null,
  search: string | undefined | null,
): BrandCode => {
  if (!hostname) {
    return DEFAULT_BRAND;
  }

  const lowered = hostname.toLowerCase();
  if (LOCALHOST_HOSTNAMES.includes(lowered)) {
    return resolveBrandOverrideFromSearch(search) ?? DEFAULT_BRAND;
  }

  return resolveBrandFromHostname(hostname);
};

/**
 * Get the current brand from browser hostname.
 *
 * Safe for server/test environments (returns default if window not available).
 */
export const getBrandCode = (): BrandCode => {
  if (typeof window === 'undefined' || !window.location) {
    return DEFAULT_BRAND;
  }
  return resolveBrandFromLocation(window.location.hostname, window.location.search);
};
